import React from "react";
import {useStores} from "../../../../Hooks";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import country from 'country-list-js';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import Tooltip from '@mui/material/Tooltip';
import {WorkspaceDatePicker} from "./generalSettings/WorkspaceDatePicker"
import {WorkspaceOptionsPicker} from "./generalSettings/WorkspaceOptionsPicker";
import {observer} from "mobx-react";
import {RenderInput} from "./generalSettings/renderOtherSettings"
import {SettingsHeader} from "./Utils/SettingsHeader";
import ConditionalComponent from "../../../Shared/ConditionalComponent";

export const SubWorkspaceGeneralSettings = observer(({showHeader}) => {
    const {generalStore, workspaceManagementStore, tableStore} = useStores()

    const getInputIcon = (tooltipText)=>{
        return (<Tooltip title={tooltipText}
                         componentsProps={{tooltip: {
                             className: '!bg-main-normal color-white font-bold text-m',
                             },
                 }}>
            <ErrorOutlineRoundedIcon sx={{color: 'var(--main-normal-color)', fontSize: '24px', cursor: 'pointer'}}/>
        </Tooltip>)
    }
    const settings = [
        {
            title: 'Name',
            placeholder: 'Add workspace name',
            value: workspaceManagementStore.editedDomain?.WorkspaceDisplayedName,
            Icon: getInputIcon('Workspace name must be 3–24 characters long and may include letters, numbers, spaces, and symbols like !, @, ™, etc.'),
            onChange: (e) => {
                const newValue = e.target.value
                workspaceManagementStore.editedDomain = {
                    ...workspaceManagementStore.editedDomain,
                    WorkspaceDisplayedName: newValue
                }
                workspaceManagementStore.validationEditsState.WorkspaceDisplayedName = workspaceManagementStore.isNameImmediateValid()

            },
            type:'text',
            length:'50',
            MaxLength: 24,
            fieldInDb: 'WorkspaceDisplayedName',
            notValidText: "Invalid workspace name.",
            isInputCount: true,
        },
        {
            title: 'Owner',
            placeholder: 'Add owner email Address',
            value: workspaceManagementStore.editedOwner,
            Icon: getInputIcon('Invitation will be sent by email once changes are saved'),
            onChange: (e) => {
                const newValue = e.target.value
                workspaceManagementStore.editedOwner = newValue
                workspaceManagementStore.validationEditsState.Owner = true

            },
            type: 'email',
            length:'50',
            MaxLength: 50,
            fieldInDb: 'Owner',
            notValidText: 'Not valid email format.',
        },
        {
            title: 'Expiration date',
            value: workspaceManagementStore.getExpirationDate,
            onChange: (newValue) => {
                workspaceManagementStore.editedDomain = {...workspaceManagementStore.editedDomain, ExpirationDate: new Date(newValue)}
            },
            type: 'date',
            fieldInDb: 'ExpirationDate',
            minDate: 1,
        },
        {
            title: 'Country',
            placeholder: 'Select a country',
            value: workspaceManagementStore.editedDomain.Country,
            Icon: <KeyboardArrowDownIcon sx={{color: 'var(--main-normal-color)', fontSize: '24px', cursor: 'pointer'}}
                                       />,
            list: country.names(),
            fieldInDb: 'Country',
        },
        {
            title: 'Number of members allowed',
            placeholder: 'Enter a Number of users',
            value: workspaceManagementStore.editedDomain?.MaxUsers,
            Icon: getInputIcon(generalStore.availableSeats ?`You have ${JSON.stringify(generalStore.availableSeats)} available seats` : 'No available seats.'),
            onChange: (e) => {
                const newValue = Number(e.target.value)
                const currentValue = tableStore.currentRow?.MaxUsers ?? 0
                workspaceManagementStore.validationEditsState.MaxUsers = true
                if (newValue >= 0) {
                    workspaceManagementStore.editedDomain = {
                        ...workspaceManagementStore.editedDomain,
                        MaxUsers: Number(newValue)
                    }
                } if (!Number.isInteger(Number(newValue)) || newValue <= 0 || newValue > (generalStore.availableSeats+ (!showHeader ? 0 : currentValue)) || ((generalStore.availableSeats === 0) && !showHeader)) {
                    workspaceManagementStore.validationEditsState.MaxUsers = false
                } else {
                    workspaceManagementStore.validationEditsState.MaxUsers = true
                }
            },
            type:'number',
            length:'30',
            fieldInDb: 'MaxUsers',
            notValidText: (generalStore.availableSeats === 0 && !showHeader) ?  "No free member seats available." : `Allowed number of members is between 1 and ${generalStore.availableSeats+ (!showHeader ? 0 : tableStore.currentRow?.MaxUsers ?? 0)}.`,
        }

    ]
    const settingsWrap = (settings, innerComponent) => {
        const validated = workspaceManagementStore.validationEditsState[settings.fieldInDb];
        return (
            <div key={settings.fieldInDb} className={`flex flex-${settings.flex} w-full justify-between p-1 text-xs ${settings.flex === 'row' ? 'items-center': 'gap-2'}`}>
                <div className="flex flex-row justify-bettween">
                    <div>{settings.title}</div>
                    <ConditionalComponent if={settings.isInputCount}>
                        <div className="absolute top-30 right-12">{settings.isInputCount ? settings.value.length: ''}/24</div>
                    </ConditionalComponent>
                </div>
                <div className={`flex flex-col w-${settings.width} items-${showHeader ? 'end' : 'start'}`}>
                    <div
                        className={`flex flex-row justify-between items-center border-2 ${validated ? 'border-panels-normal' : 'border-leavedeletenew-normal'} rounded-input justify-between ${settings.type === 'date' ? 'pl-2 py-2' : 'p-2'}`}
                        style = {{width: settings.width === 'full' ? '100%' : settings.length === '30' ? '100px' :'100%'}}
                    >
                        {innerComponent}
                    </div>
                    {!validated && settings.notValidText && (
                        <div className={`text-leavedeletenew-normal text-${showHeader ? 'end' : 'start'}`}>
                            {settings.notValidText}
                        </div>
                    )}
                </div>

            </div>
        )
    }
    const renderSettingsByType =(settings, index)=>{
        let flex = 'rows';
        let width = '50';
        if (workspaceManagementStore.isWorkspaceNew) {
            flex = 'col';
            width = 'full';
        }
        settings.flex= flex;
        settings.width = width;

        switch (settings.title) {
            case 'Country':
                return (settingsWrap(settings, <WorkspaceOptionsPicker key={index} settings={settings}
                                                             />))
            case 'Expiration date':
                if (workspaceManagementStore.isWorkspaceNew || generalStore.domainSettings?.CanEditExpirationDate) {
                    return (settingsWrap(settings, <WorkspaceDatePicker key={index} settings={settings}/>))
                }
                break;
            case 'Number of members allowed':
                return (settingsWrap(settings, <RenderInput key={index} settings={settings}/>))
            case  'Owner':
                return (settingsWrap(settings, <RenderInput key={index} settings={settings}/>))
                break;
            case 'Name':
                if (workspaceManagementStore.isWorkspaceNew) {
                    return (settingsWrap(settings, <RenderInput key={index} settings={settings}/>))
                }
                break;
            default:
                return null;
        }
    }
    return (
        <section className="flex-grow flex-col bg-inputfield-normal rounded-menu p-2 font-sans">
            <ConditionalComponent if={showHeader}>
                <SettingsHeader title={'General Settings'} Icon={SettingsOutlinedIcon}/>
            </ConditionalComponent>
            {settings.map((setting, index) => (renderSettingsByType(setting, index)))}
        </section>
    );
});